import {
  useDashboard,
  useMe,
  useQuery,
  useTeam,
  useTeamRole,
} from '@vrplatform/data';
import { useAppName } from '@vrplatform/theme';
import { Anchor, Loader, Text } from '@mantine/core';
import {
  pluralize,
  whereAutomationTemplateVisibility,
  whereReservations,
} from '@vrplatform/ui-common';
import { Link } from 'react-router-dom';
import { Fragment } from 'react/jsx-runtime';

type Props = {
  connectionId: string;
  tenantId: string;
};

type ConnectionEntityLinksQueryData = NonNullable<
  ReturnType<typeof useConnectionEntityLinksQuery>['data']
>;

type ConnectionEntityKey = keyof ConnectionEntityLinksQueryData;

const getLink = (connectionId: string, key: ConnectionEntityKey) => {
  const links: Record<ConnectionEntityKey, string> = {
    reservations: `/reservations?connectionIds=${connectionId}`,
    ownerStatements: `/owner-statements?connectionId=${connectionId}`,
    automations: `/automations?connectionId=${connectionId}`,
    listings: `/listings?pmsConnectionId=${connectionId}`,
    payments: `/payments?connectionId=${connectionId}`,
    deposits: `/deposits?connectionId=${connectionId}`,
    expenses: `/expenses?connectionId=${connectionId}`,
    transfers: `/transfers`,
  };

  return links[key];
};

const useConnectionEntityLinksQuery = ({ connectionId, tenantId }: Props) => {
  const { id: meId } = useMe();
  const [{ isGL }] = useTeam();
  const [dashboard] = useDashboard();
  const { isPartnerAdmin, isSuperAdmin, isVrpAdmin } = useTeamRole();

  return useQuery(
    (q, args) => {
      const reservations =
        q
          .reservationAggregate({
            where: whereReservations({
              GL: args.isGL,
              tenantId: args.tenantId,
              dashboard: args.dashboard,
              ownerPortalContactId: null,
              includeLines: false,
              connectionIds: [args.connectionId],
            }),
          })
          ?.aggregate?.count() ?? 0;

      const ownerStatements =
        q
          .ownerStatementAggregate({
            where: {
              tenantId: { _eq: args.tenantId },
              automation: { connectionId: { _eq: args.connectionId } },
            },
          })
          ?.aggregate?.count() ?? 0;

      const automations =
        q
          .automationAggregate({
            where: {
              tenantId: { _eq: args.tenantId },
              ttemplate: {
                visibility: whereAutomationTemplateVisibility({
                  isPartnerAdmin: args.isPartnerAdmin,
                  isSuperAdmin: args.isSuperAdmin,
                  isVrpAdmin: args.isVrpAdmin,
                }),
              },
              _or: [
                {
                  leftConnectionId: { _eq: args.connectionId },
                },
                {
                  rightConnectionId: { _eq: args.connectionId },
                },
                {
                  connectionId: { _eq: args.connectionId },
                },
              ],
            },
          })
          ?.aggregate?.count() ?? 0;

      const listings =
        q
          .listingAggregate({
            where: {
              tenantId: { _eq: args.tenantId },
              _or: [
                {
                  connections: {
                    connectionId: { _eq: args.connectionId },
                  },
                },
                {
                  connectionId: { _eq: connectionId },
                },
              ],
            },
          })
          ?.aggregate?.count() ?? 0;

      const payments =
        q
          .paymentAggregate({
            where: {
              tenantId: { _eq: args.tenantId },
              connectionId: { _eq: args.connectionId },
            },
          })
          ?.aggregate?.count() ?? 0;

      const deposits =
        q
          .transactionAggregate({
            where: {
              tenantId: { _eq: args.tenantId },
              connectionId: { _eq: args.connectionId },
              type: { _eq: 'deposit' },
            },
          })
          ?.aggregate?.count() ?? 0;

      const expenses =
        q
          .transactionAggregate({
            where: {
              tenantId: { _eq: args.tenantId },
              connectionId: { _eq: args.connectionId },
              type: { _eq: 'expense' },
            },
          })
          ?.aggregate?.count() ?? 0;

      const transfers =
        q
          .transactionAggregate({
            where: {
              tenantId: { _eq: args.tenantId },
              connectionId: { _eq: args.connectionId },
              type: { _eq: 'transfer' },
            },
          })
          ?.aggregate?.count() ?? 0;

      return {
        reservations,
        ownerStatements,
        automations,
        listings,
        payments,
        deposits,
        expenses,
        transfers,
      };
    },
    {
      keepPreviousData: true,
      skip: !connectionId,
      variables: {
        connectionId,
        isGL,
        dashboard,
        meId,
        isPartnerAdmin,
        isSuperAdmin,
        isVrpAdmin,
        tenantId,
      },
      queryKey: ['connections', 'connection-entity-links'],
    }
  );
};

export const ConnectionEntityLinks = (props: Props) => {
  const { isLoading, data: aggregates } = useConnectionEntityLinksQuery(props);

  const { appName } = useAppName();

  const { connectionId } = props;

  if (!aggregates || isLoading) return <Loader size={14} />;

  if (Object.values(aggregates).some(Boolean) === false)
    return <Text c="gray">No synced data in {appName}</Text>;

  return (
    <Text>
      {Object.entries(aggregates)
        .filter(([, v]) => v)
        .map(([k, value], index, arr) => {
          const key = k as keyof typeof aggregates;

          const hasNext = index < arr.length - 1;

          if (!value) return null;

          let label: React.ReactNode = null;

          if (key === 'ownerStatements')
            label = (
              <Text span>
                {value} {pluralize(value, 'owner statement')}
              </Text>
            );
          else
            label = (
              <Anchor component={Link} to={getLink(connectionId, key)}>
                {value} {pluralize(value, key.slice(0, -1), key)}
              </Anchor>
            );

          return (
            <Fragment key={key}>
              {label}
              {hasNext && ', '}
            </Fragment>
          );
        })}
    </Text>
  );
};
